import React, { useMemo, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AppLayout } from '../components/layout/AppLayout'
import { PageContainer } from '../components/layout/PageContainer'
import { Box, Button } from '../components/ui/foundation'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { cn } from '../lib/utils'
import { useVisionBoards } from '../hooks/useVisionBoards'
import { useAuth } from '../hooks/useAuth'
import { useMobileDetection } from '../hooks/useMobileDetection'
import {
  MOBILE_BOTTOM_SHEET_DESTRUCTIVE_ROW_CLASS,
  MOBILE_BOTTOM_SHEET_ROW_CLASS,
  MobileBottomSheet,
} from '../components/mobile/MobileBottomSheet'
import type { Database } from '../types/database'
import { MetaTags } from '../components/seo/MetaTags'

type VisionBoard = Database['public']['Tables']['vision_boards']['Row']

const EMPTY_BOARD_TITLE = 'Untitled'

const formatEditedLabel = (dateString: string): string => {
  const value = new Date(dateString)
  if (Number.isNaN(value.getTime())) return 'Edited recently'

  const now = new Date()
  const diffMs = now.getTime() - value.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))

  if (diffMins < 1) return 'Edited just now'
  if (diffMins < 60) return `Edited ${diffMins} min ago`

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  )
  const startOfDate = new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate()
  )
  const diffDays = Math.floor(
    (startOfToday.getTime() - startOfDate.getTime()) / (1000 * 60 * 60 * 24)
  )

  if (diffDays <= 0) return 'Edited today'
  if (diffDays === 1) return 'Edited yesterday'
  if (diffDays < 7) return `Edited ${diffDays} days ago`
  if (diffDays < 14) return 'Edited last week'
  if (diffDays < 28) return `Edited ${Math.floor(diffDays / 7)} weeks ago`

  const sameYear = value.getFullYear() === now.getFullYear()
  if (sameYear) {
    return `Edited ${value.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
  }
  return `Edited ${value.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
}

const DraftChip: React.FC<{
  board: VisionBoard
  onRename: (id: string, title: string) => Promise<void>
  onDuplicate: (id: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
  showPersistentActions?: boolean
  usePhoneActionSheet?: boolean
}> = ({
  board,
  onRename,
  onDuplicate,
  onDelete,
  showPersistentActions = false,
  usePhoneActionSheet = false,
}) => {
  const [isRenaming, setIsRenaming] = useState(false)
  const [draftTitle, setDraftTitle] = useState(board.title || EMPTY_BOARD_TITLE)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [actionSheetOpen, setActionSheetOpen] = useState(false)
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isRenaming) return
    const timer = setTimeout(() => {
      renameInputRef.current?.focus()
      renameInputRef.current?.select()
    }, 0)
    return () => clearTimeout(timer)
  }, [isRenaming])

  const commitRename = async () => {
    const nextTitle = draftTitle.trim() || EMPTY_BOARD_TITLE
    await onRename(board.id, nextTitle)
    setDraftTitle(nextTitle)
    setIsRenaming(false)
  }

  return (
    <>
      <article
        role="listitem"
        className="group relative flex items-center gap-3 rounded-[16px] border border-[rgba(36,34,30,0.06)] bg-white px-4 py-3 shadow-sm transition-all duration-settle ease-precise hover:shadow-md"
      >
        {!isRenaming ? (
          <Link
            to={`/board/${board.id}`}
            className="absolute inset-0 z-10 rounded-[16px]"
            aria-label={`Open ${board.title || EMPTY_BOARD_TITLE}`}
          />
        ) : null}
        {isRenaming ? (
          <input
            ref={renameInputRef}
            className="relative z-20 min-w-0 flex-1 bg-transparent font-sans text-fs-400 text-ink-700 outline-none"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={() => void commitRename()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void commitRename()
              if (e.key === 'Escape') {
                setDraftTitle(board.title || EMPTY_BOARD_TITLE)
                setIsRenaming(false)
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="min-w-0 flex-1 truncate font-sans text-fs-400 text-ink-600">
            {board.title || EMPTY_BOARD_TITLE}
          </span>
        )}

        <span className="shrink-0 font-sans text-fs-300 text-ink-400">
          {formatEditedLabel(board.updated_at)}
        </span>

        {usePhoneActionSheet ? (
          <button
            type="button"
            className={cn(
              'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
              'text-ink-400 hover:bg-[rgba(36,34,30,0.04)] hover:text-ink-700',
              showPersistentActions
                ? 'opacity-100'
                : 'opacity-0 transition-opacity duration-settle group-hover:opacity-100',
              'focus:outline-none focus-visible:outline-none',
              'relative z-20'
            )}
            onClick={(e) => {
              e.stopPropagation()
              setActionSheetOpen(true)
            }}
            aria-label="Draft options"
            aria-haspopup="dialog"
          >
            <svg
              className="h-4 w-4"
              fill="currentColor"
              viewBox="0 0 256 256"
              aria-hidden="true"
            >
              <circle cx="128" cy="60" r="12" />
              <circle cx="128" cy="128" r="12" />
              <circle cx="128" cy="196" r="12" />
            </svg>
          </button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                  'text-ink-400 hover:bg-[rgba(36,34,30,0.04)] hover:text-ink-700',
                  showPersistentActions
                    ? 'opacity-100'
                    : 'opacity-0 transition-opacity duration-settle group-hover:opacity-100',
                  'focus:outline-none focus-visible:outline-none',
                  'relative z-20'
                )}
                onClick={(e) => e.stopPropagation()}
                aria-label="Draft options"
                aria-haspopup="menu"
              >
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                  aria-hidden="true"
                >
                  <circle cx="128" cy="60" r="12" />
                  <circle cx="128" cy="128" r="12" />
                  <circle cx="128" cy="196" r="12" />
                </svg>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-40"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={() => {
                  setDraftTitle(board.title || EMPTY_BOARD_TITLE)
                  setIsRenaming(true)
                }}
              >
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => void onDuplicate(board.id)}>
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </article>

      <MobileBottomSheet
        open={actionSheetOpen}
        onOpenChange={setActionSheetOpen}
        title="Draft actions"
      >
        <div className="space-y-5">
          <div>
            <p className="truncate font-sans text-fs-400 font-medium text-ink-700">
              {board.title || EMPTY_BOARD_TITLE}
            </p>
            <p className="mt-1 font-sans text-fs-300 text-ink-500">
              {formatEditedLabel(board.updated_at)}
            </p>
          </div>

          <div className="divide-y divide-[rgba(36,34,30,0.06)]">
            <button
              type="button"
              className={MOBILE_BOTTOM_SHEET_ROW_CLASS}
              onClick={() => {
                setActionSheetOpen(false)
                setDraftTitle(board.title || EMPTY_BOARD_TITLE)
                setIsRenaming(true)
              }}
            >
              <span>Rename</span>
            </button>
            <button
              type="button"
              className={MOBILE_BOTTOM_SHEET_ROW_CLASS}
              onClick={() => {
                setActionSheetOpen(false)
                void onDuplicate(board.id)
              }}
            >
              <span>Duplicate</span>
            </button>
          </div>

          <div className="border-t border-[rgba(36,34,30,0.06)] pt-2">
            <button
              type="button"
              className={MOBILE_BOTTOM_SHEET_DESTRUCTIVE_ROW_CLASS}
              onClick={() => {
                setActionSheetOpen(false)
                setConfirmDeleteOpen(true)
              }}
            >
              <span>Delete</span>
            </button>
          </div>
        </div>
      </MobileBottomSheet>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete draft?</DialogTitle>
            <DialogDescription>
              "{board.title || EMPTY_BOARD_TITLE}" will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                void onDelete(board.id)
                setConfirmDeleteOpen(false)
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export const DraftsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isPhone } = useMobileDetection()

  const { boards, loading, error, renameBoard, duplicateBoard, deleteBoard } =
    useVisionBoards(user?.id)

  const draftBoards = useMemo(
    () =>
      [...boards]
        .filter((b) => !b.thumbnail_url)
        .sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        ),
    [boards]
  )

  const handleDuplicateBoard = async (boardId: string) => {
    const newBoardId = await duplicateBoard(boardId)
    if (!newBoardId) return
    navigate(`/board/${newBoardId}`)
  }

  return (
    <AppLayout>
      <MetaTags
        title="Drafts | Printboard"
        description="Make your vision feel closer with drafts you can revisit anytime."
        noindex={true}
      />
      <PageContainer
        maxWidth="full"
        className="px-4 pb-12 pt-8 md:px-6 lg:px-8 xl:px-12"
      >
        <h1 className="sr-only">Drafts</h1>

        <Box>
          {error ? (
            <div className="py-space-4xl text-center">
              <p className="mb-2 font-sans text-fs-400 text-ink-700">
                Unable to load your drafts.
              </p>
              <p className="mb-space-lg text-fs-300 text-ink-400">{error}</p>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </div>
          ) : draftBoards.length === 0 && !loading ? (
            <div className="mx-auto flex min-h-[68vh] w-full max-w-[400px] items-center justify-center">
              <p className="font-sans text-fs-400 text-ink-400">No drafts.</p>
            </div>
          ) : (
            <div className="mx-auto max-w-[1200px]">
              <p className="mb-4 font-sans text-fs-300 text-ink-400">Drafts</p>
              <div role="list" className="flex flex-col gap-2">
                {loading && draftBoards.length === 0
                  ? Array.from({ length: 4 }).map((_, index) => (
                      <div
                        key={index}
                        aria-hidden="true"
                        className="h-[54px] rounded-[16px] border border-[rgba(36,34,30,0.06)] bg-white px-4 py-3 shadow-sm"
                      />
                    ))
                  : draftBoards.map((board) => (
                      <DraftChip
                        key={board.id}
                        board={board}
                        onRename={renameBoard}
                        onDuplicate={handleDuplicateBoard}
                        onDelete={deleteBoard}
                        showPersistentActions={isPhone}
                        usePhoneActionSheet={isPhone}
                      />
                    ))}
              </div>
            </div>
          )}
        </Box>
      </PageContainer>
    </AppLayout>
  )
}

export default DraftsPage
